import { scheduleInputToInstant } from "@common/utils/adsetSchedule";
import {
  SCHEDULED_BUDGET_MAX_SEGMENTS,
  SCHEDULED_BUDGET_MIN_SEGMENTS,
  SCHEDULED_BUDGET_MIN_SEGMENTS_MESSAGE,
  SCHEDULED_BUDGET_PHASE_MIN_DURATION_HOURS,
  SCHEDULED_BUDGET_PHASE_MIN_DURATION_SECONDS,
} from "@common/utils/campaignBudgetMeta";
import type { BudgetPhaseForm } from "../components/GuidedBudgetEditor/BudgetSelector";
import type { WallTimedBudgetSegmentLike } from "./scheduledBudgetWallFlightValidation";

export type ScheduledBudgetPhaseTimelineIssue = {
  /** Path under the budget object (RHF / Yup), e.g. `segments[0].time_end`. */
  pathSuffix: string;
  message: string;
};

/**
 * When the first / last phased row stores an empty wall time but the UI locks that side to the
 * ad flight (campaign start / end), validation should still treat the boundary as set.
 */
export type ScheduledBudgetPhaseTimelineImplicitFlightWalls = {
  firstPhaseStartWall?: string | null;
  lastPhaseEndWall?: string | null;
};

type SegmentLike = Pick<
  WallTimedBudgetSegmentLike,
  "time_start" | "time_end"
>;

function resolvedSegmentWalls(
  s: SegmentLike,
  index: number,
  segmentCount: number,
  implicit: ScheduledBudgetPhaseTimelineImplicitFlightWalls | undefined
): { start: string; end: string } {
  const rawStart = s.time_start?.trim() ?? "";
  const rawEnd = s.time_end?.trim() ?? "";
  const start =
    index === 0 &&
    !rawStart &&
    implicit?.firstPhaseStartWall?.trim()
      ? implicit.firstPhaseStartWall.trim()
      : rawStart;
  const end =
    index === segmentCount - 1 &&
    !rawEnd &&
    implicit?.lastPhaseEndWall?.trim()
      ? implicit.lastPhaseEndWall.trim()
      : rawEnd;
  return { start, end };
}

const phaseLabel = (i: number) => `Phase ${i + 1}`;

function getScheduledPhasePositiveAmountIssue(
  segments: Array<{ amount?: { amount?: unknown } | null } | null | undefined>
): ScheduledBudgetPhaseTimelineIssue | null {
  for (let i = 0; i < segments.length; i++) {
    const raw = segments[i]?.amount?.amount;
    const n =
      raw != null && String(raw).trim() !== ""
        ? Number(String(raw).trim())
        : NaN;
    if (!Number.isFinite(n) || n <= 0) {
      return {
        pathSuffix: `segments[${i}].amount.amount`,
        message: "Each phase needs a positive budget amount",
      };
    }
  }
  return null;
}

/**
 * Yup / RHF timeline checks on event-wall form values (filled windows, ordering, contiguity).
 * Persisted UTC rules live in {@link validateBudgetSegments} / launch & publish APIs.
 */
export function getScheduledBudgetPhaseTimelineIssue(
  segments: SegmentLike[] | undefined,
  eventTimezone: string | undefined,
  implicitFlightWalls?: ScheduledBudgetPhaseTimelineImplicitFlightWalls | null
): ScheduledBudgetPhaseTimelineIssue | null {
  const tz = (eventTimezone ?? "").trim() || "UTC";
  const implicit = implicitFlightWalls ?? undefined;
  const segs = segments ?? [];
  if (segs.length > SCHEDULED_BUDGET_MAX_SEGMENTS) {
    return {
      pathSuffix: "segments",
      message: `At most ${SCHEDULED_BUDGET_MAX_SEGMENTS} budget phases are allowed.`,
    };
  }
  if (segs.length < SCHEDULED_BUDGET_MIN_SEGMENTS) {
    return {
      pathSuffix: "segments",
      message: SCHEDULED_BUDGET_MIN_SEGMENTS_MESSAGE,
    };
  }

  for (let i = 0; i < segs.length; i++) {
    const s = segs[i];
    const rawStart = s.time_start?.trim() ?? "";
    const rawEnd = s.time_end?.trim() ?? "";
    const { start, end } = resolvedSegmentWalls(s, i, segs.length, implicit);
    if (!start || !end) {
      const missingStart = !rawStart && !start;
      const missingEnd = !rawEnd && !end;
      if (missingStart && missingEnd) {
        return {
          pathSuffix: `segments[${i}].time_start`,
          message: `${phaseLabel(i)} needs a start time and an end time`,
        };
      }
      if (missingStart) {
        return {
          pathSuffix: `segments[${i}].time_start`,
          message: `${phaseLabel(i)} needs a start time`,
        };
      }
      return {
        pathSuffix: `segments[${i}].time_end`,
        message: `${phaseLabel(i)} needs an end time`,
      };
    }
    const startInst = scheduleInputToInstant(start, tz);
    const endInst = scheduleInputToInstant(end, tz);
    if (!startInst || !endInst || !endInst.isAfter(startInst)) {
      return {
        pathSuffix: `segments[${i}].time_end`,
        message: `${phaseLabel(i)} must end after it starts`,
      };
    }
    const durationSec =
      (endInst.valueOf() - startInst.valueOf()) / 1000;
    if (durationSec < SCHEDULED_BUDGET_PHASE_MIN_DURATION_SECONDS) {
      return {
        pathSuffix: `segments[${i}].time_end`,
        message: `${phaseLabel(i)} must span at least ${SCHEDULED_BUDGET_PHASE_MIN_DURATION_HOURS} hours from start to end.`,
      };
    }
    if (i < segs.length - 1) {
      const wA = resolvedSegmentWalls(s, i, segs.length, implicit);
      const wB = resolvedSegmentWalls(segs[i + 1], i + 1, segs.length, implicit);
      const endA = scheduleInputToInstant(wA.end, tz);
      const startB = scheduleInputToInstant(wB.start, tz);
      if (!endA || !startB) {
        return {
          pathSuffix: `segments[${i}].time_end`,
          message: `${phaseLabel(i)} and ${phaseLabel(i + 1)} need valid times so the schedule stays contiguous.`,
        };
      }
      const va = endA.valueOf();
      const vb = startB.valueOf();
      if (va !== vb) {
        if (va > vb) {
          return {
            pathSuffix: `segments[${i}].time_end`,
            message: `${phaseLabel(i)} overlaps ${phaseLabel(i + 1)}: end this phase exactly when the next one begins.`,
          };
        }
        return {
          pathSuffix: `segments[${i}].time_end`,
          message: `${phaseLabel(i)} leaves a gap before ${phaseLabel(i + 1)}: the next phase must start when this one ends.`,
        };
      }
    }
  }

  return null;
}

/** Plain message for inline UI (Engage modal, GuidedBudgetEditor fallback). */
export function getScheduledBudgetPhaseTimelineError(
  segments: BudgetPhaseForm[] | undefined,
  eventTimezone: string | undefined,
  implicitFlightWalls?: ScheduledBudgetPhaseTimelineImplicitFlightWalls | null
): string | null {
  return (
    getScheduledBudgetPhaseTimelineIssue(
      segments,
      eventTimezone,
      implicitFlightWalls
    )?.message ?? null
  );
}

/** Timeline + positive phase amounts for Yup (wall-time form only). */
export function validateScheduledBudgetWallFormFields(
  segments: WallTimedBudgetSegmentLike[],
  eventTimezone: string | undefined,
  implicitFlightWalls?: ScheduledBudgetPhaseTimelineImplicitFlightWalls | null
): ScheduledBudgetPhaseTimelineIssue | null {
  const tz = (eventTimezone ?? "").trim() || "UTC";
  const timelineError = getScheduledBudgetPhaseTimelineIssue(
    segments,
    tz,
    implicitFlightWalls
  );
  if (timelineError) return timelineError;
  return getScheduledPhasePositiveAmountIssue(segments);
}
